import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import {
  useGetNotificationDeliveryPreferencesQuery,
  useSaveNotificationDeliveryPreferencesMutation,
} from "notifications/delivery-preferences/_api/notification-delivery-preferences-api";
import type {
  NotificationDelivery,
  NotificationDeliveryPreferencesJSON,
  NotificationKind,
} from "notifications/delivery-preferences/_schemas/notification-delivery-preferences-schema";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useSession } from "users/__session/session-context";
import SettingsSection from "users/settings/_views/settings-section";

export const NOTIFICATION_PREFERENCES_LOADING_MESSAGE = "Loading your notification choices.";
export const NOTIFICATION_PREFERENCES_LOAD_FAILED_MESSAGE = "Your notification choices could not be loaded.";
export const NOTIFICATION_PREFERENCES_SAVE_FAILED_MESSAGE = "Your notification choice was not saved. Try again.";
export const NO_EMAIL_DIGEST_MESSAGE =
  "You get no reminder email. Every kind of notification reaches you in the app only.";

/** What a person calls each kind, and what it covers. The order is the order the API sends. */
export const NOTIFICATION_KIND_TEXT: Record<NotificationKind, { label: string; detail: string }> = {
  Messages: { label: "Messages", detail: "New messages in your conversations." },
  Invitations: { label: "Invitations", detail: "Invitations to a conversation, a team, a firm or your network." },
  Proposals: { label: "Proposals", detail: "Proposals you draft, review, send or receive." },
  Agreements: { label: "Work agreements", detail: "The work agreements you are on." },
  Marketplace: { label: "Marketplace", detail: "Your marketplace teams and profile." },
  Connections: { label: "Connections", detail: "New connections in your network." },
  Other: { label: "Other notifications", detail: "Every notification that is not one of the kinds above." },
};

const DELIVERY_LABELS: Record<NotificationDelivery, string> = {
  InAppAndEmail: "In the app and by email",
  InAppOnly: "In the app only",
};

/**
 * How each kind of notification reaches the person, and whether they get the reminder email.
 *
 * Every notification shows in the notification center. The only email is the reminder that lists
 * notifications still unread, so the choice is whether that email names a kind, and whether it is
 * sent at all. "Email only" is not offered: the email links to the center, and a notification kept
 * out of the center would leave it nothing to link to (#6152).
 */
export default function NotificationSettingsSection() {
  const session = useSession();
  const preferencesQuery = useGetNotificationDeliveryPreferencesQuery({ session }, { skip: !session?.isLoggedIn });
  const [savePreferences] = useSaveNotificationDeliveryPreferencesMutation();
  const preferences = preferencesQuery.data;

  const save = (next: NotificationDeliveryPreferencesJSON) => {
    savePreferences({ session, preferences: next })
      .unwrap()
      .catch(() => enqueueSnackbar(NOTIFICATION_PREFERENCES_SAVE_FAILED_MESSAGE, { variant: "error" }));
  };

  return (
    <SettingsSection
      description="Every notification shows in your notification center. Attorney Hub can also email you a reminder about the notifications you have not read. Your account keeps these choices."
      title="Notifications"
    >
      {preferences ? (
        <NotificationChoices onChange={save} preferences={preferences} />
      ) : (
        <PreferencesStatus failed={preferencesQuery.isError} onRetry={() => void preferencesQuery.refetch()} />
      )}
    </SettingsSection>
  );
}

function PreferencesStatus(props: Readonly<{ failed: boolean; onRetry: () => void }>) {
  if (!props.failed) {
    return <Typography color="text.secondary" variant="body2">{NOTIFICATION_PREFERENCES_LOADING_MESSAGE}</Typography>;
  }

  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Typography color="error" variant="body2">{NOTIFICATION_PREFERENCES_LOAD_FAILED_MESSAGE}</Typography>
      <Button onClick={props.onRetry} variant="outlined">Try again</Button>
    </Stack>
  );
}

function NotificationChoices(props: Readonly<{
  preferences: NotificationDeliveryPreferencesJSON;
  onChange: (next: NotificationDeliveryPreferencesJSON) => void;
}>) {
  const { preferences } = props;

  const chooseDelivery = (kind: NotificationKind, delivery: NotificationDelivery) => {
    props.onChange({
      ...preferences,
      kinds: preferences.kinds.map((item) => (item.kind === kind ? { kind, delivery } : item)),
    });
  };

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            checked={preferences.emailDigestEnabled}
            onChange={(_event, checked) => props.onChange({ ...preferences, emailDigestEnabled: checked })}
          />
        }
        label="Email me a reminder about unread notifications"
      />
      {!preferences.emailDigestEnabled && (
        <Typography color="text.secondary" variant="body2">{NO_EMAIL_DIGEST_MESSAGE}</Typography>
      )}
      <Stack spacing={2}>
        {preferences.kinds.map(({ kind, delivery }) => (
          <KindChoice
            delivery={delivery}
            disabled={!preferences.emailDigestEnabled}
            key={kind}
            kind={kind}
            onChange={(next) => chooseDelivery(kind, next)}
          />
        ))}
      </Stack>
    </>
  );
}

function KindChoice(props: Readonly<{
  kind: NotificationKind;
  delivery: NotificationDelivery;
  disabled: boolean;
  onChange: (delivery: NotificationDelivery) => void;
}>) {
  const text = NOTIFICATION_KIND_TEXT[props.kind];
  // With no reminder email, nothing reaches the person by email, whatever they chose for the kind.
  const shown = props.disabled ? "InAppOnly" : props.delivery;

  return (
    <FormControl component="fieldset" disabled={props.disabled}>
      <FormLabel component="legend" sx={{ color: "text.primary" }}>{text.label}</FormLabel>
      <Typography color="text.secondary" variant="body2">{text.detail}</Typography>
      <RadioGroup
        onChange={(_event, value) => props.onChange(value as NotificationDelivery)}
        row
        value={shown}
      >
        {(Object.keys(DELIVERY_LABELS) as NotificationDelivery[]).map((delivery) => (
          <FormControlLabel control={<Radio />} key={delivery} label={DELIVERY_LABELS[delivery]} value={delivery} />
        ))}
      </RadioGroup>
    </FormControl>
  );
}
